import { HostedSidebar } from '../../../components/hosted/sidebar';
import HostedActionRow from '../../../components/hosted/ActionRow';
import HostedLanguageSelector from '../../../components/hosted/LanguageSelector';
import HostedClientLogo from '../../../components/hosted/ClientLogo';
import HostedMobileLayout from '../../../layouts/MobileLayout';
import { useLayoutData } from '../../../hooks/useLayoutContext';
import { useTranslation } from '../../../i18n/i18n';
import { type FlowStartPageProps } from '../../types';

/**
 * HostedMobileFlowStartPage — Mobile entry page shown before app selection / Chulak flow.
 *
 * Displays sidebar content inline (title, description, steps, requested data, terms)
 * with a sticky CTA footer at the bottom. Uses HostedMobileLayout with a custom header.
 *
 * Figma screens:
 *   Single app:    https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=176-22554
 *   Multiple apps: https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=293-17552
 */
export default function HostedMobileFlowStartPage({
  singleApp,
  onContinue,
  onCancel,
}: FlowStartPageProps) {
  const { t } = useTranslation();
  const {
    client,
    requestType,
    interactionConfig,
    requestedDataGroups,
    requestedDataLoading,
  } = useLayoutData();

  const customHeader = (
    <header className="flex items-center justify-between border-b border-border bg-background px-4 py-3 shrink-0">
      {singleApp ? (
        <>
          <div className="flex flex-1 items-center justify-start">
            <HostedClientLogo client={client} />
          </div>
          <div className="flex items-center justify-end">
            <HostedLanguageSelector compact />
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-1 items-center" />
          <div className="flex items-center justify-center shrink-0">
            <HostedClientLogo client={client} />
          </div>
          <div className="flex flex-1 items-center justify-end">
            <HostedLanguageSelector compact />
          </div>
        </>
      )}
    </header>
  );

  return (
    <HostedMobileLayout
      header={customHeader}
      contentClassName="bg-sidebar px-4"
      footer={
        <HostedActionRow
          primary={{
            label: t('mobileFlowStart.startVerification'),
            onClick: onContinue,
          }}
          secondary={{ label: t('agentStart.cancel'), onClick: onCancel }}
        />
      }
    >
      <HostedSidebar
        client={client}
        requestType={requestType}
        interactionConfig={interactionConfig}
        requestedDataGroups={singleApp ? requestedDataGroups : undefined}
        requestedDataLoading={singleApp ? requestedDataLoading : false}
        variant="mobile"
      />
    </HostedMobileLayout>
  );
}
